<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import ceoPhoto from '../assets/Profile.jpeg'
import logoImg from '../assets/logo.png'
import scaffoldPhoto1 from '../assets/1.jpg'
import home1 from '../assets/Home.jpg'
import home2 from '../assets/Home2.jpeg'
import home3 from '../assets/Home3.jpeg'
import gallery1 from '../assets/gallery1.jpeg'
import gallery2 from '../assets/gallery2.png'
import gallery3 from '../assets/gallery3.jpeg'
import gallery4 from '../assets/gallery4.png'
import gallery5 from '../assets/gallery5.jpg'
import info2 from '../assets/infomation2.jpeg'
import info3 from '../assets/infomation3.jpeg'

const slides = [home1, home2, home3];
const currentSlide = ref(0);
const galleryPhotos = [gallery1, gallery2, gallery3, gallery4, gallery5];
let slideInterval = null;

const services = [
    {
        title: '新築足場工事',
		subTitle: '建物をカタチにするための「最高の一歩目」',
        image: scaffoldPhoto1,
        description: '新築工事における足場は、現場で一番最初に形になり、最後まで職人たちを支え続ける重要なインフラです。足場架設の専門として、「建てるための足場」ではなく「最高に作業しやすい足場」を追求しています。'
    },
    {
        title: '改修足場工事',
		subTitle: '建物の未来を守る、安全と品質の土台',
        image: home2,
        description: '外壁塗装、屋上防水、大規模修繕工事——。建物の寿命を延ばし、資産価値を維持するために欠かせない「改修工事」において、最も重要な役割を担うのが足場です。',
        description2: '私たちは、作業者の命を守る「安全性」はもちろん、そこに住まう方々や近隣の皆様への「安心感」を第一に考えた足場架設をご提供します。'
    },
    {
        title: '仮囲い工事',
		subTitle: '現場の安全と、街の景観を守る「境界線」',
        image: info2,
        description: '工事現場と外部を仕切る「仮囲い」は、第三者の安全を守るだけでなく、その現場の「顔」としての役割も担っています。',
        description2: '単に囲うだけではなく、強風に負けない堅牢な設置と、街並みに調和する美しく整然とした施工をご提供します。'
	},
    {
        title: '防犯カメラ取付電気工事',
		subTitle: 'プロの視点で見守る、確かな安心と安全',
        image: info3,
        description: '「大切な家族や資産を守りたい」「トラブルを未然に防ぎたい」',
        description2: '防犯カメラは、今の時代に欠かせない安心のパートナーです。私たちは、単にカメラを取り付けるだけでなく、防犯のプロの視点で「死角を作らない」「犯罪を未然に防ぐ」最適なセキュリティ環境をご提案いたします。'
    },
];

onMounted(() => {
	const sections = document.querySelectorAll('section.section');
	const observer = new window.IntersectionObserver(
		(entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					entry.target.classList.add('slide-in');
				} else {
					entry.target.classList.remove('slide-in');
				}
			});
		},
		{
			threshold: 0.15
		}
	);
	sections.forEach(section => observer.observe(section));
	slideInterval = setInterval(() => {
		currentSlide.value = (currentSlide.value + 1) % slides.length;
	}, 3000);
});

onUnmounted(() => {
	if (slideInterval) clearInterval(slideInterval);
});
</script>

<template>
	<div class="main-container">
		<!-- Hero Section -->
		<section id="hero" class="hero-section">
			<div
				v-for="(slide, index) in slides"
				:key="index"
				class="hero-bg"
				:style="{ backgroundImage: `url(${slide})` }"
				:class="{ 'hero-bg-active': index === currentSlide }"
			></div>
			<div class="hero-overlay"></div>
			<div class="hero-content">
				<img :src="logoImg" alt="RSJ株式会社ロゴ" class="hero-logo">
				<h1 class="hero-title">RSJ株式会社</h1>
				<p class="hero-subtitle">企業理念 ～人を想い、和を創る。～</p>
				<p class="hero-description">
					東京・神奈川・埼玉を中心とした関東近郊で鳶工事一式、電気設備工事を行っております
				</p>
			</div>
		</section>

		<!-- Business Content Section -->
		<section id="business-content" class="section business-content">
			<div class="container">
				<h2 class="section-title">事業内容</h2>
				<div class="business-description">
					<p>
						弊社の鳶工事・足場工事は要領は勿論、安全管理を徹底し重傷事故が多い仕事だからこそ<br>
						一日一日を大事に無事故で仕事を終えることにフォーカスしています。<br>
						そのために従業員の方々には安全に仕事をしてもらうための環境を整えることはもちろん、<br>従業員の方々が仕事以外の面でも充実した生活を送れるような環境も整えていきたいと考えております。<br>
					</p>
				</div>
				<div class="service-grid">
					<div
						v-for="service in services"
						:key="service.title"
						class="service-card"
					>
						<div class="service-image-wrap">
							<img :src="service.image" :alt="service.title" class="service-image">
						</div>
						<div class="service-card-body">
							<h3>{{ service.title }}</h3>
							<h4>{{ service.subTitle }}</h4>
							<p>{{ service.description }}</p>
							<p>{{ service.description2 }}</p>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Philosophy Section -->
		<section id="philosophy" class="section philosophy">
			<div class="container">
				<h2 class="section-title">経営理念</h2>
				<div class="philosophy-catchphrase">
					<p class="catchphrase-main">安心の土台を築き、未来を支える。</p>
					<p class="catchphrase-main">関わるすべての人を、大切にできる心の余裕を。</p>
				</div>
				<div class="philosophy-triangle">
					<div class="triangle-top">
						<div class="philosophy-card">
							<div class="card-label">ミッション</div>
							<div class="card-sublabel">使命</div>
							<p>現場の安全と暮らしの安全、二つの「完全安心」を提供し、地域社会の基盤を支えます。</p>
						</div>
					</div>
					<div class="triangle-connector">
						<span class="connector-line connector-left"></span>
						<span class="connector-line connector-right"></span>
					</div>
					<div class="triangle-bottom">
						<div class="philosophy-card">
							<div class="card-label">ビジョン</div>
							<div class="card-sublabel">展望</div>
							<p>従業員が心身ともに充実し、誇りを持って働ける「建設業界の新しいスタンダード」を目指します。</p>
							<p>社員ファーストの徹底：社員がストレスなく、前向きに働ける環境こそが、最高の施工を生む源泉であると考えます。</p>
						</div>
						<div class="philosophy-card">
							<div class="card-label">バリュー</div>
							<div class="card-sublabel">価値観</div>
							<p>「人には合う・合わないがある」ことを認め合い、それぞれの強みを活かせる適材適所の現場配置で、最高のパフォーマンスを発揮します。</p>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Gallery Section -->
		<section id="gallery" class="section gallery">
			<div class="container">
				<h2 class="section-title">ギャラリー</h2>
				<div class="gallery-grid">
					<div v-for="(photo, index) in galleryPhotos" :key="index" class="gallery-item">
						<img :src="photo" :alt="`ギャラリー${index + 1}`" class="gallery-image">
					</div>
				</div>
			</div>
		</section>

		<!-- CEO Message Section -->
		<section id="ceo-message" class="section ceo-message">
			<div class="container">
				<h2 class="section-title">代表からのメッセージ</h2>
				<div class="ceo-content">
					<div class="ceo-photo">
						<img :src="ceoPhoto" alt="代表取締役社長 小林 竜貴" class="ceo-image">
					</div>
					<div class="ceo-text">
						<div class="message-content">
							<p>この度は、RSJ株式会社のホームページをご覧いただき、誠にありがとうございます。</p>
							<p>私のキャリアは美容師から始まりました。24歳の時、結婚を機に「家族を守れる強い基盤を作りたい」と決意し、友人の紹介で建設業界へ飛び込みました。全くの別世界でしたが、そこで出会った職人の技術と誇りに魅了され、今日まで歩んできました。<br>
							現場での経験を積み、数年を経て、志を共にする大切な仲間と出会い、当社を設立した今、私が最も大切にしているのは「人」です。</p>
							<h3>「社員に、人間関係などの理不尽な我慢をさせたくない」</h3><br>
							<p>これが私の経営哲学の原点です。社員が前向きな気持ちで現場に向かえるよう、一人一人の適性や相性を最大限に考慮した環境づくりを徹底しています。</p>
							<p>会社が大きくなることはもちろん大事で目標の1つです。それと同時に、社員全員が「この会社で働けて良かった」と心から笑い、家族や友人を大切にできる。そんな「心の余裕」を持てる会社でありたい。
							私は、社員の幸せが最高の施工品質を生むと信じ、これからも現場一つ一つに誠実に向き合ってまいります。</p>
							<p>
								最後に、少し私的な話になりますが、私の父はゴルフのレッスンプロを営んでおります。<br>
								僕自身もゴルフは幼い頃からやらせていただき、大好きで今ではかけがえのない趣味です。<br>
								そんな父の背中を見ながら育ち、私がゴルフから学んだのは、「常に自分自身と誠実に向き合うこと」、そして「一打一打を妥協せず、丁寧に積み重ねる大切さ」でした。<br>
								ビジネスというフィールドにおいても、この精神を忘れることなく、皆様との信頼関係を何よりも大切にしながら、一歩一歩確実に会社を成長させていく所存です。</p>
							<p>今後とも変わらぬご指導ご鞭撻を賜りますよう、よろしくお願い申し上げます。</p>
							<div class="company-policies">
								<p><strong>～代表挨拶～</strong></p>
								<div class="policy-item">
									<h4>人を想い、和（輪）を創る。</h4>
									<p>最高の技術を支えるのは、妥協のない信頼関係。<br>
									私は単なる現場の仲間だけではなく大切な人を大切にしたいと考えております。<br>
									一緒に協力していただける方をお待ちしてます。</p>
								</div>
							</div>
							<div class="ceo-signature">
								<p><strong>代表取締役社長 小林 竜貴</strong></p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
		<!-- Company Overview Section -->
		<section id="company-overview" class="section company-overview">
			<div class="container">
				<h2 class="section-title">会社概要</h2>
				<div class="overview-grid">
					<div class="overview-item">
						<h3>会社名</h3>
						<p>RSJ株式会社</p>
					</div>
					<div class="overview-item">
						<h3>代表取締役社長</h3>
						<p>小林 竜貴</p>
					</div>
					<div class="overview-item">
						<h3>所在地</h3>
						<p>東京都日野市旭が丘６－３－３６</p>
					</div>
					<div class="overview-item">
						<h3>創業/創立</h3>
						<p>2017年4月1日/2025年3月1日</p>
					</div>
					<div class="overview-item">
						<h3>主な事業内容</h3>
						<p>足場工事</p>
					</div>
					<div class="overview-item">
						<h3>登録番号</h3>
						<p>T2012801023829</p>
					</div>
					<div class="overview-item">
						<h3>TEL</h3>
						<p>080-4426-2237</p>
					</div>
					<div class="overview-item">
						<h3>Mail</h3>
						<p>masakiryuki947@gmail.com</p>
					</div>
					<div class="overview-item">
						<h3>LINE</h3>
						<p>@owi7912d</p>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<style scoped>
	* {
		margin: 0;
		padding: 0;
		box-sizing: border-box;
	}

	.main-container {
		font-family: 'Noto Serif JP', 'Hiragino Mincho ProN', 'Yu Mincho', 'YuMincho', serif;
		line-height: 1.6;
		color: #ffffff;
		background-color: #000000;
	}

	.hero-section {
		position: relative;
		color: white;
		padding: 100px 0;
		text-align: center;
		min-height: 80vh;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.hero-bg {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		opacity: 0;
		transition: opacity 1s ease-in-out;
	}

	.hero-bg-active {
		opacity: 1;
	}

	.hero-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(26,26,26,0.6) 60%, rgba(255,128,48,0.2) 100%);
	}

	.hero-content {
		position: relative;
		z-index: 1;
		max-width: 800px;
		margin: 0 auto;
		padding: 0 20px;
	}

	.hero-logo {
		width: 120px;
		height: 120px;
		object-fit: contain;
		margin-bottom: 20px;
		filter: drop-shadow(2px 2px 6px rgba(0,0,0,0.3));
	}

	.hero-title {
		font-size: 3.5rem;
		font-weight: bold;
		margin-bottom: 20px;
		text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
	}

	.hero-subtitle {
		font-size: 1.5rem;
		margin-bottom: 20px;
		font-weight: 300;
	}

	.hero-description {
		font-size: 1.1rem;
		margin-bottom: 40px;
		opacity: 0.9;
	}

	.hero-buttons {
		display: flex;
		gap: 20px;
		justify-content: center;
		flex-wrap: wrap;
	}

	.btn-primary, .btn-secondary {
		padding: 15px 30px;
		font-size: 1.1rem;
		border: none;
		border-radius: 50px;
		cursor: pointer;
		transition: all 0.3s ease;
		text-decoration: none;
		display: inline-block;
	}

	.btn-primary {
		background: #ff6b6b;
		color: white;
	}

	.btn-primary:hover {
		background: #ff5252;
		transform: translateY(-2px);
	}

	.btn-secondary {
		background: transparent;
		color: white;
		border: 2px solid white;
	}

	.btn-secondary:hover {
		background: white;
		color: #ff8030;
	}

	.section {
		padding: 80px 0;
	}

	section.section {
		opacity: 0;
		visibility: hidden;
		transform: translateX(-60px);
		transition: opacity 0.7s cubic-bezier(.77,0,.18,1),
					transform 0.7s cubic-bezier(.77,0,.18,1),
					visibility 0s linear 0.7s;
	}

	section.section.slide-in {
		opacity: 1;
		visibility: visible;
		transform: translateX(0);
		transition: opacity 0.7s cubic-bezier(.77,0,.18,1),
					transform 0.7s cubic-bezier(.77,0,.18,1),
					visibility 0s;
	}

	.container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px;
	}

	.section-title {
		font-size: 2.5rem;
		text-align: center;
		margin-bottom: 50px;
		color: #ffffff;
		position: relative;
	}

	.section-title::after {
		content: '';
		display: block;
		width: 80px;
		height: 4px;
		background: #ff8030;
		margin: 20px auto 0;
		border-radius: 2px;
	}

	.company-overview {
		background: #090909;
	}

	.overview-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
		gap: 30px;
	}

	.overview-item {
		background: #0f0f0f;
		padding: 30px;
		border-radius: 10px;
		box-shadow: 0 5px 15px rgba(0,0,0,0.3);
		transition: transform 0.3s ease;
		border-left: 3px solid rgba(255,128,48,0.4);
	}

	.overview-item:hover {
		transform: translateY(-5px);
		background: #363636;
		border-left-color: #ff8030;
	}

	.overview-item h3 {
		color: #ff8030;
		font-size: 1.3rem;
		margin-bottom: 10px;
		font-weight: 600;
	}

	.overview-item p {
		font-size: 1.1rem;
		color: #e0e0e0;
	}

	.business-content {
		background: #000000;
	}

	.business-description {
		max-width: 800px;
		margin: 0 auto 50px;
		text-align: center;
	}

	.business-description p {
		font-size: 1.1rem;
		line-height: 1.8;
		color: #e0e0e0;
	}

	.service-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 28px;
		max-width: 900px;
		margin: 0 auto;
	}

	.service-card {
		background: #0f0f0f;
		border-radius: 15px;
		overflow: hidden;
		box-shadow: 0 5px 20px rgba(0,0,0,0.4);
		transition: transform 0.3s ease, box-shadow 0.3s ease;
	}

	.service-card:hover {
		transform: translateY(-6px);
		box-shadow: 0 10px 30px rgba(255,128,48,0.3);
	}

	.service-image-wrap {
		width: 100%;
		height: 200px;
		overflow: hidden;
	}

	.service-image {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.3s ease;
		display: block;
	}

	.service-card:hover .service-image {
		transform: scale(1.05);
	}

	.service-card-body {
		padding: 24px;
		background: #0f0f0f;
		border-top: 3px solid #ff8030;
	}

	.service-card-body h3 {
		font-size: 1.3rem;
		margin-bottom: 12px;
		color: #ffffff;
		font-weight: 700;
	}

	.service-card-body p {
		font-size: 1rem;
		line-height: 1.6;
		color: #cccccc;
	}

	/* Process Section */
	.process-section {
		margin-top: 3rem;
		background: #0f0f0f;
		padding: 2.5rem;
		border-radius: 15px;
		box-shadow: 0 10px 30px rgba(0,0,0,0.4);
	}

	.process-section-title {
		font-size: 1.8rem;
		color: #ffffff;
		text-align: center;
		margin-bottom: 2rem;
		padding-bottom: 1rem;
		border-bottom: 2px solid #ff8030;
	}

	.process-item {
		margin-bottom: 2rem;
	}

	.process-title {
		font-size: 1.3rem;
		color: #ff8030;
		margin-bottom: 1rem;
		font-weight: 600;
	}

	.process-description {
		font-size: 1rem;
		color: #cccccc;
		line-height: 1.6;
		margin-bottom: 1.5rem;
	}

	.process-photos {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
		gap: 1.5rem;
		margin-top: 1rem;
	}

	.photo-item {
		border-radius: 10px;
		overflow: hidden;
		box-shadow: 0 5px 15px rgba(0,0,0,0.1);
	}

	.process-image {
		width: 100%;
		height: 250px;
		object-fit: cover;
		transition: transform 0.3s ease;
	}

	.process-image:hover {
		transform: scale(1.05);
	}

	.philosophy {
		background: #090909;
	}

	.philosophy-catchphrase {
		text-align: center;
		margin-bottom: 60px;
	}

	.catchphrase-main {
		font-size: 1.7rem;
		font-weight: bold;
		color: #ffffff;
		margin-bottom: 14px;
		letter-spacing: 0.02em;
	}

	.catchphrase-sub {
		font-size: 1.15rem;
		color: #bbbbbb;
		font-style: italic;
	}

	.philosophy-triangle {
		max-width: 860px;
		margin: 0 auto;
	}

	.triangle-top {
		display: flex;
		justify-content: center;
		margin-bottom: 0;
	}

	.triangle-top .philosophy-card {
		max-width: 420px;
		width: 100%;
	}

	.triangle-connector {
		display: flex;
		justify-content: center;
		position: relative;
		height: 40px;
		margin: 0 auto;
		max-width: 860px;
	}

	.connector-line {
		display: block;
		position: absolute;
		top: 0;
		width: 2px;
		height: 100%;
		background: linear-gradient(to bottom, #ff8030, rgba(255,128,48,0.3));
	}

	.connector-line.connector-left {
		left: calc(50% - 210px);
		transform-origin: top center;
		transform: rotate(20deg);
	}

	.connector-line.connector-right {
		right: calc(50% - 210px);
		transform-origin: top center;
		transform: rotate(-20deg);
	}

	.triangle-bottom {
		display: flex;
		gap: 32px;
		justify-content: center;
	}

	.triangle-bottom .philosophy-card {
		flex: 1;
		max-width: 400px;
	}

	.philosophy-card {
		background: #0f0f0f;
		border-radius: 15px;
		padding: 32px;
		box-shadow: 0 5px 20px rgba(0,0,0,0.5);
		border: 1px solid rgba(255,128,48,0.2);
		position: relative;
		overflow: hidden;
	}

	.philosophy-card::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 3px;
		background: linear-gradient(90deg, #ff8030, #ffb080);
	}

	.philosophy-card:hover {
		border-color: rgba(255,128,48,0.5);
		box-shadow: 0 8px 30px rgba(255,128,48,0.15);
		transform: translateY(-4px);
		transition: all 0.3s ease;
	}

	.card-label {
		font-size: 1.4rem;
		font-weight: bold;
		color: #ff8030;
		margin-bottom: 4px;
		letter-spacing: 0.05em;
	}

	.card-sublabel {
		font-size: 0.85rem;
		color: #888888;
		margin-bottom: 18px;
		letter-spacing: 0.1em;
	}

	.philosophy-card p {
		font-size: 1rem;
		line-height: 1.9;
		color: #e0e0e0;
		margin-bottom: 0.8rem;
	}

	.philosophy-card p:last-child {
		margin-bottom: 0;
	}

	/* Gallery Section */
	.gallery {
		background: #000000;
	}

	.gallery-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 20px;
		max-width: 1000px;
		margin: 0 auto;
	}

	.gallery-item {
		border-radius: 10px;
		overflow: hidden;
		box-shadow: 0 5px 15px rgba(0,0,0,0.1);
	}

	.gallery-image {
		width: 100%;
		height: 280px;
		object-fit: cover;
		transition: transform 0.3s ease;
		display: block;
	}

	.gallery-image:hover {
		transform: scale(1.05);
	}

	/* CEO Message Section */
	.ceo-message {
		background: linear-gradient(135deg, #060606 0%, #0e0e0e 100%);
		padding: 80px 0;
	}

	.ceo-content {
		display: flex;
		gap: 3rem;
		align-items: flex-start;
		max-width: 1200px;
		margin: 0 auto;
	}

	.ceo-photo {
		flex: 0 0 300px;
	}

	.ceo-image {
		width: 100%;
		height: 400px;
		object-fit: cover;
		border-radius: 15px;
		box-shadow: 0 10px 30px rgba(0,0,0,0.2);
	}

	.ceo-text {
		flex: 1;
		background: #0f0f0f;
		padding: 2.5rem;
		border-radius: 15px;
		box-shadow: 0 10px 30px rgba(0,0,0,0.4);
		border: 1px solid rgba(255,128,48,0.15);
	}

	.message-content p {
		margin-bottom: 1.5rem;
		line-height: 1.8;
		color: #e0e0e0;
		font-size: 1rem;
	}

	.company-policies {
		margin: 2rem 0;
		padding: 1.5rem;
		background: #181818;
		border-radius: 10px;
		border-left: 4px solid #ff8030;
	}

	.policy-item {
		margin-bottom: 1.5rem;
	}

	.policy-item h4 {
		color: #ff8030;
		font-size: 1.1rem;
		margin-bottom: 0.5rem;
		font-weight: 600;
	}

	.policy-item p {
		margin-bottom: 0.5rem;
		color: #cccccc;
	}

	.ceo-signature {
		text-align: right;
		margin-top: 2rem;
		padding-top: 1rem;
		border-top: 2px solid rgba(255,128,48,0.3);
	}

	.ceo-signature p {
		color: #e0e0e0;
		font-size: 1.1rem;
		margin-bottom: 0;
	}

	@media (max-width: 768px) {
		.hero-title {
			font-size: 2.5rem;
		}

		.hero-subtitle {
			font-size: 1.2rem;
		}

		.section-title {
			font-size: 2rem;
		}

		.hero-buttons {
			flex-direction: column;
			align-items: center;
		}

		.btn-primary, .btn-secondary {
			width: 80%;
			max-width: 300px;
		}

		.overview-grid {
			grid-template-columns: 1fr;
		}

		.gallery-grid {
			grid-template-columns: 1fr;
		}

		.service-grid {
			grid-template-columns: 1fr;
		}

		.catchphrase-main {
			font-size: 1.2rem;
		}

		.catchphrase-sub {
			font-size: 1rem;
		}

		.triangle-top {
			margin-bottom: 20px;
		}

		.triangle-top .philosophy-card {
			max-width: 100%;
		}

		.triangle-bottom {
			flex-direction: column;
			gap: 20px;
		}

		.triangle-bottom .philosophy-card {
			max-width: 100%;
		}

		.triangle-connector {
			display: none;
		}

		/* CEO Message モバイル対応 */
		.ceo-content {
			flex-direction: column;
			gap: 2rem;
		}

		.ceo-photo {
			flex: none;
			max-width: 250px;
			margin: 0 auto;
		}

		.ceo-image {
			height: 300px;
		}

		.ceo-text {
			padding: 1.5rem;
		}

		.company-policies {
			padding: 1rem;
		}

		/* Process Section モバイル対応 */
		.process-section {
			margin-top: 2rem;
			padding: 1.5rem;
		}

		.process-section-title {
			font-size: 1.5rem;
		}

		.process-photos {
			grid-template-columns: 1fr;
			gap: 1rem;
		}

		.process-image {
			height: 200px;
		}
	}
</style>
